import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { Copy, X } from 'lucide-react';
import type { Workspace } from '@molt/shared';
import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface BoardCopyDialogSubmit {
  readonly workspaceId: string;
  readonly visibility: 'private' | 'workspace_visible' | 'public';
  readonly keepCards: boolean;
  readonly title: string;
  readonly titleEdited: boolean;
}

export interface BoardCopyDialogProps {
  readonly open: boolean;
  readonly boardTitle: string;
  readonly workspaces: readonly Workspace[];
  readonly defaultWorkspaceId: string;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly cardsCopied?: number | undefined;
  readonly copiedBoardPath?: string | undefined;
  readonly onCancel: () => void;
  readonly onCopy: (input: BoardCopyDialogSubmit) => void;
}

type BoardCopyVisibility = BoardCopyDialogSubmit['visibility'];

/**
 * ⚠ A FUNCTION OF `t`, where this was a module-level `const` array of literals —
 * a module constant cannot call `t`, the `WorkspaceSwitcher` /
 * `BOARD_ROLE_LABELS` ruling. Each entry holds a literal `t('…')` call rather
 * than a key looked up from a record, so all six values stay visible to the
 * `t('…')` anchor the routing pin counts with.
 */
function visibilityChoices(t: InterfaceLanguageContextValue['t']): readonly {
  readonly value: BoardCopyVisibility;
  readonly label: string;
  readonly description: string;
}[] {
  return [
    {
      value: 'private',
      label: t('copyMove.board.visibility.private'),
      description: t('copyMove.board.visibility.privateBody'),
    },
    {
      value: 'workspace_visible',
      label: t('copyMove.board.visibility.workspace'),
      description: t('copyMove.board.visibility.workspaceBody'),
    },
    {
      value: 'public',
      label: t('copyMove.board.visibility.public'),
      description: t('copyMove.board.visibility.publicBody'),
    },
  ];
}

export function BoardCopyDialog({
  open,
  boardTitle,
  workspaces,
  defaultWorkspaceId,
  loading,
  busy,
  cardsCopied,
  copiedBoardPath,
  onCancel,
  onCopy,
}: BoardCopyDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const titleField = useRef<HTMLInputElement | null>(null);
  const formId = useId();
  const workspaceSelectId = useId();
  const visibilityLegendId = useId();
  const cleanTitle = boardTitle.trim() === '' ? t('copyMove.untitledBoard') : boardTitle;

  const [title, setTitle] = useState(cleanTitle);
  const [titleEdited, setTitleEdited] = useState(false);
  const [workspaceId, setWorkspaceId] = useState('');
  const [visibility, setVisibility] = useState<BoardCopyVisibility>('workspace_visible');
  const [keepCards, setKeepCards] = useState(true);

  const defaultDestinationWorkspaceId = useMemo(() => {
    if (workspaces.some((workspace) => workspace.id === defaultWorkspaceId)) {
      return defaultWorkspaceId;
    }
    return workspaces[0]?.id ?? '';
  }, [defaultWorkspaceId, workspaces]);

  const selectedWorkspaceId = workspaces.some((workspace) => workspace.id === workspaceId)
    ? workspaceId
    : defaultDestinationWorkspaceId;
  const visibilityOptions = useMemo(() => visibilityChoices(t), [t]);

  useEffect(() => {
    if (!open) return;
    setTitle(cleanTitle);
    setTitleEdited(false);
    setWorkspaceId(defaultDestinationWorkspaceId);
    setVisibility('workspace_visible');
    setKeepCards(true);
  }, [cleanTitle, defaultDestinationWorkspaceId, open]);

  if (!open) return null;

  const titleError = title.trim().length === 0 ? t('copyMove.titleRequired') : null;
  const hasWorkspaces = workspaces.length > 0;
  const controlsDisabled = busy === true || loading === true || !hasWorkspaces;
  const confirmDisabled =
    controlsDisabled || selectedWorkspaceId === '' || titleError !== null;
  const disabledReason =
    loading === true
      ? t('copyMove.board.loadingWorkspaces')
      : !hasWorkspaces
        ? t('copyMove.board.needsWorkspace')
        : (titleError ?? undefined);
  const disabledReasonProps = disabledReason ? { disabledReason } : {};
  const busyProps = busy === undefined ? {} : { busy };
  const resultVisible = cardsCopied !== undefined || copiedBoardPath !== undefined;

  function submitCopy() {
    if (confirmDisabled) return;
    onCopy({
      workspaceId: selectedWorkspaceId,
      visibility,
      keepCards,
      title: title.trim(),
      titleEdited,
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('copyMove.board.title', { title: cleanTitle })}
      description={t('copyMove.board.description')}
      initialFocus={titleField}
      closeControl={
        <IconButton label={t('copyMove.closeCopy')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {resultVisible ? t('copyMove.done') : t('copyMove.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconStart={<Copy aria-hidden="true" className="size-4" />}
            busyLabel={t('copyMove.board.busy')}
            disabled={confirmDisabled}
            type="submit"
            form={formId}
            {...busyProps}
            {...disabledReasonProps}
          >
            {t('copyMove.board.submit')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submitCopy();
        }}
      >
        {resultVisible ? (
          <Notice
            tone="info"
            title={t('copyMove.board.copiedTitle')}
            body={
              cardsCopied === undefined
                ? t('copyMove.board.copiedReady')
                : t('copyMove.board.copiedCount', { count: cardsCopied }, cardsCopied)
            }
            action={
              copiedBoardPath === undefined ? undefined : (
                <Button as="a" size="sm" variant="secondary" href={copiedBoardPath}>
                  {t('copyMove.board.openCopy')}
                </Button>
              )
            }
          />
        ) : null}

        <TextField
          ref={titleField}
          label={t('copyMove.board.titleLabel')}
          value={title}
          disabled={controlsDisabled}
          onChange={(event) => {
            setTitleEdited(true);
            setTitle(event.currentTarget.value);
          }}
          {...(titleError === null ? {} : { error: titleError })}
        />

        {hasWorkspaces ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={workspaceSelectId} className="text-sm font-medium text-ink">
              {t('copyMove.board.workspaceLabel')}
            </label>
            <select
              id={workspaceSelectId}
              value={selectedWorkspaceId}
              disabled={controlsDisabled}
              onChange={(event) => setWorkspaceId(event.currentTarget.value)}
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
            >
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name.trim() === ''
                    ? t('copyMove.untitledWorkspace')
                    : workspace.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('copyMove.board.noWorkspaces')}
            body={t('copyMove.board.needsWorkspace')}
          />
        )}

        <fieldset
          className="flex flex-col gap-2"
          aria-labelledby={visibilityLegendId}
          disabled={controlsDisabled}
        >
          <legend id={visibilityLegendId} className="text-sm font-medium text-ink">
            {t('copyMove.board.visibility')}
          </legend>
          <div className="flex flex-col gap-1">
            {visibilityOptions.map((choice) => (
              <label
                key={choice.value}
                className="flex items-start gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink"
              >
                <input
                  type="radio"
                  name="board-copy-visibility"
                  value={choice.value}
                  aria-label={choice.label}
                  checked={visibility === choice.value}
                  onChange={() => setVisibility(choice.value)}
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span>{choice.label}</span>
                  <span className="text-xs text-ink-muted">{choice.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex items-start gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink">
          <input
            type="checkbox"
            aria-label={t('copyMove.board.keepCards')}
            checked={keepCards}
            disabled={controlsDisabled}
            onChange={(event) => setKeepCards(event.currentTarget.checked)}
          />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span>{t('copyMove.board.keepCards')}</span>
            <span className="text-xs text-ink-muted">
              {t('copyMove.board.keepCardsBody')}
            </span>
          </span>
        </label>
      </form>
    </Dialog>
  );
}
